import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { accountAPI } from '../../utils/crmApi';
import toast from 'react-hot-toast';
import { 
    useFormValidation, 
    ValidationSummary, 
    ValidatedInput, 
    ValidatedTextarea, 
    ValidatedSelect,
    FloatingValidationAlert,
    createValidationRules,
    VALIDATION_PATTERNS
} from '../ui/enhanced-form-validation';
import {
    BuildingOfficeIcon,
    EnvelopeIcon,
    PhoneIcon,
    GlobeAltIcon,
    MapPinIcon,
    TagIcon,
    CheckCircleIcon,
    XMarkIcon,
    ArrowLeftIcon,
    UserGroupIcon
} from '@heroicons/react/24/outline';

const AccountForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = !!(id && id !== 'new');
    
    console.log('🏢 AccountForm rendered:', { id, isEditing, pathname: window.location.pathname });

    const [loading, setLoading] = useState(false);
    
    // Enhanced form validation
    const validationRules = createValidationRules({
        name: {
            required: true,
            requiredMessage: 'Company name is required',
            minLength: 2,
            minLengthMessage: 'Company name must be at least 2 characters'
        },
        'primaryContact.name': {
            required: true,
            requiredMessage: 'Primary contact name is required'
        },
        'primaryContact.email': {
            required: true,
            requiredMessage: 'Primary contact email is required',
            pattern: VALIDATION_PATTERNS.email,
            patternMessage: 'Please enter a valid email address'
        },
        'primaryContact.phoneE164': {
            pattern: VALIDATION_PATTERNS.phone,
            patternMessage: 'Phone must be in E.164 format (e.g., +1234567890)'
        },
        website: {
            pattern: VALIDATION_PATTERNS.url,
            patternMessage: 'Website must be a valid URL (e.g., https://example.com)'
        },
        'address.line1': {
            required: true,
            requiredMessage: 'Address line 1 is required'
        },
        'address.city': {
            required: true,
            requiredMessage: 'City is required'
        },
        'address.region': {
            required: true,
            requiredMessage: 'Region/State is required'
        },
        'address.postalCode': {
            required: true,
            requiredMessage: 'Postal code is required'
        }
    });

    const {
        formData,
        errors,
        touched,
        showValidationSummary,
        setShowValidationSummary,
        updateField,
        validateAll,
        getFieldError,
        hasErrors,
        errorCount,
        setFormData,
        setErrors
    } = useFormValidation({
        // Basic Information
        name: '',
        size: '1-5',
        website: '',
        domain: '',
        
        // Address Information
        address: {
            line1: '',
            line2: '',
            city: '',
            region: '',
            postalCode: '',
            country: 'ES'
        },
        
        // Primary Contact
        primaryContact: {
            name: '',
            email: '',
            phoneE164: ''
        },
        
        // Business Information
        ownerUserId: '',
        lifecycle: 'lead',
        segment: 'SMB',
        
        // Tags and Notes
        tags: [],
        notes: '',
        
        // Deduplication Fields
        vatId: '',
        taxId: '',
        
        // SLA Configuration
        sla: {
            responseHours: 24,
            priority: 'P2'
        },
        
        // Consent
        consent: {
            email: false,
            sms: false,
            lawfulBasis: 'consent'
        }
    }, validationRules);

    const sizeOptions = [
        { value: '1-5', label: '1-5 employees' },
        { value: '5-10', label: '5-10 employees' },
        { value: '10-50', label: '10-50 employees' },
        { value: '50-100', label: '50-100 employees' },
        { value: '100-1000', label: '100-1000 employees' },
        { value: '1000+', label: '1000+ employees' }
    ];

    const lifecycleOptions = [
        { value: 'lead', label: 'Lead', color: 'yellow' },
        { value: 'prospect', label: 'Prospect', color: 'blue' },
        { value: 'customer', label: 'Customer', color: 'green' },
        { value: 'churned', label: 'Churned', color: 'red' }
    ];

    const segmentOptions = [
        { value: 'Agency', label: 'Agency' },
        { value: 'Corp', label: 'Corporate' },
        { value: 'SMB', label: 'Small & Medium Business' }
    ];

    const priorityOptions = [
        { value: 'P1', label: 'P1 - Critical' },
        { value: 'P2', label: 'P2 - High' },
        { value: 'P3', label: 'P3 - Medium' },
        { value: 'P4', label: 'P4 - Low' }
    ];

    const countryOptions = [
        { value: 'ES', label: 'Spain' },
        { value: 'FR', label: 'France' },
        { value: 'DE', label: 'Germany' },
        { value: 'IT', label: 'Italy' },
        { value: 'GB', label: 'United Kingdom' },
        { value: 'US', label: 'United States' },
        { value: 'CA', label: 'Canada' },
        { value: 'AU', label: 'Australia' },
        { value: 'NL', label: 'Netherlands' },
        { value: 'BE', label: 'Belgium' },
        { value: 'CH', label: 'Switzerland' },
        { value: 'AT', label: 'Austria' },
        { value: 'SE', label: 'Sweden' },
        { value: 'NO', label: 'Norway' },
        { value: 'DK', label: 'Denmark' },
        { value: 'FI', label: 'Finland' },
        { value: 'PT', label: 'Portugal' },
        { value: 'IE', label: 'Ireland' },
        { value: 'LU', label: 'Luxembourg' },
        { value: 'MT', label: 'Malta' },
        { value: 'CY', label: 'Cyprus' },
        { value: 'GR', label: 'Greece' },
        { value: 'PL', label: 'Poland' },
        { value: 'CZ', label: 'Czech Republic' },
        { value: 'HU', label: 'Hungary' },
        { value: 'SK', label: 'Slovakia' },
        { value: 'SI', label: 'Slovenia' },
        { value: 'HR', label: 'Croatia' },
        { value: 'RO', label: 'Romania' },
        { value: 'BG', label: 'Bulgaria' },
        { value: 'LT', label: 'Lithuania' },
        { value: 'LV', label: 'Latvia' },
        { value: 'EE', label: 'Estonia' }
    ];

    useEffect(() => {
        if (isEditing) {
            fetchAccount();
        }
    }, [id]);

    const fetchAccount = async () => {
        try {
            setLoading(true);
            const response = await accountAPI.getById(id);
            if (response.data.success) {
                const account = response.data.data;
                setFormData({
                    name: account.name || '',
                    size: account.size || '1-5',
                    website: account.website || '',
                    domain: account.domain || '',
                    address: {
                        line1: account.address?.line1 || '',
                        line2: account.address?.line2 || '',
                        city: account.address?.city || '',
                        region: account.address?.region || '',
                        postalCode: account.address?.postalCode || '',
                        country: account.address?.country || 'ES'
                    },
                    primaryContact: {
                        name: account.primaryContact?.name || '',
                        email: account.primaryContact?.email || '',
                        phoneE164: account.primaryContact?.phoneE164 || ''
                    },
                    ownerUserId: account.ownerUserId || '',
                    lifecycle: account.lifecycle || 'lead',
                    segment: account.segment || 'SMB',
                    tags: account.tags || [],
                    notes: account.notes || '',
                    sla: {
                        responseHours: account.sla?.responseHours || 24,
                        priority: account.sla?.priority || 'P2'
                    },
                    consent: {
                        email: account.consent?.email || false,
                        sms: account.consent?.sms || false,
                        lawfulBasis: account.consent?.lawfulBasis || 'consent'
                    }
                });
            }
        } catch (error) {
            console.error('Error fetching account:', error);
            toast.error('Failed to load account data');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name.startsWith('address.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                address: {
                    ...prev.address,
                    [field]: value
                }
            }));
        } else if (name.startsWith('primaryContact.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                primaryContact: {
                    ...prev.primaryContact,
                    [field]: value
                }
            }));
        } else if (name.startsWith('sla.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                sla: {
                    ...prev.sla,
                    [field]: type === 'number' ? parseInt(value) : value
                }
            }));
        } else if (name.startsWith('consent.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                consent: {
                    ...prev.consent,
                    [field]: type === 'checkbox' ? checked : value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleTagAdd = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const tag = e.target.value.trim();
            if (tag && !formData.tags.includes(tag)) {
                setFormData(prev => ({
                    ...prev,
                    tags: [...prev.tags, tag]
                }));
                e.target.value = '';
            }
        }
    };

    const handleTagRemove = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Company name is required';
        }

        if (!formData.primaryContact.name.trim()) {
            newErrors['primaryContact.name'] = 'Primary contact name is required';
        }

        if (!formData.primaryContact.email.trim()) {
            newErrors['primaryContact.email'] = 'Primary contact email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.primaryContact.email)) {
            newErrors['primaryContact.email'] = 'Please enter a valid email address';
        }

        if (formData.primaryContact.phoneE164 && !/^\+[1-9]\d{1,14}$/.test(formData.primaryContact.phoneE164)) {
            newErrors['primaryContact.phoneE164'] = 'Phone must be in E.164 format (e.g., +1234567890)';
        }

        if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
            newErrors.website = 'Website must be a valid URL (e.g., https://example.com)';
        }

        if (!formData.address.line1.trim()) {
            newErrors['address.line1'] = 'Address line 1 is required';
        }

        if (!formData.address.city.trim()) {
            newErrors['address.city'] = 'City is required';
        }

        if (!formData.address.region.trim()) {
            newErrors['address.region'] = 'Region/State is required';
        }

        if (!formData.address.postalCode.trim()) {
            newErrors['address.postalCode'] = 'Postal code is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateAll()) {
            setShowValidationSummary(true);
            toast.error(`Please fix ${errorCount} validation error${errorCount !== 1 ? 's' : ''} before submitting`);
            return;
        }

        try {
            setLoading(true);
            
            const accountData = {
                ...formData,
                // Ensure phone is in E.164 format
                primaryContact: {
                    ...formData.primaryContact,
                    phoneE164: formData.primaryContact.phoneE164 ? 
                        (formData.primaryContact.phoneE164.startsWith('+') ? formData.primaryContact.phoneE164 : `+${formData.primaryContact.phoneE164}`) : 
                        undefined
                }
            };

            let response;
            if (isEditing) {
                response = await accountAPI.update(id, accountData);
            } else {
                response = await accountAPI.create(accountData);
            }

            if (response.data.success) {
                toast.success(`Account ${isEditing ? 'updated' : 'created'} successfully!`);
                navigate('/accounts');
            } else {
                throw new Error(response.data.message || 'Failed to save account');
            }
        } catch (error) {
            console.error('Error saving account:', error);
            toast.error(error.message || 'Failed to save account');
        } finally {
            setLoading(false);
        }
    };

    if (loading && isEditing) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="bg-card rounded-lg shadow-sm border border-border mb-6">
                <div className="px-6 py-4 border-b border-border">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/accounts')}
                            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                        >
                            <ArrowLeftIcon className="h-5 w-5" />
                        </button>
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-blue-100 rounded-lg">
                                <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-foreground">
                                    {isEditing ? 'Edit Account' : 'Add New Account'}
                                </h1>
                                <p className="text-muted-foreground">
                                    {isEditing ? 'Update account information' : 'Create a new account record'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Validation Summary */}
            <ValidationSummary
                errors={errors}
                isVisible={showValidationSummary}
                onClose={() => setShowValidationSummary(false)}
                title="Please fix the following errors before submitting:"
            />

            {/* Floating Validation Alert */}
            <FloatingValidationAlert
                isVisible={hasErrors && !showValidationSummary}
                errorCount={errorCount}
                onDismiss={() => setShowValidationSummary(true)}
            />

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <div className="bg-card rounded-lg shadow-sm border border-border">
                    <div className="px-6 py-4 border-b border-border">
                        <h3 className="text-lg font-semibold text-foreground">Company Information</h3>
                    </div>
                    <div className="p-6 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Company Name *
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className={`w-full border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring ${
                                        errors.name ? 'border-red-300' : 'border-input'
                                    }`}
                                    placeholder="Enter company name"
                                />
                                {errors.name && (
                                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Company Size *
                                </label>
                                <select
                                    name="size"
                                    value={formData.size}
                                    onChange={handleInputChange}
                                    className="w-full border border-input rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                >
                                    {sizeOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Website
                                </label>
                                <div className="relative">
                                    <GlobeAltIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                                    <input
                                        type="url"
                                        name="website"
                                        value={formData.website}
                                        onChange={handleInputChange}
                                        className={`w-full pl-10 border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring ${
                                            errors.website ? 'border-red-300' : 'border-input'
                                        }`}
                                        placeholder="https://example.com"
                                    />
                                </div>
                                {errors.website && (
                                    <p className="mt-1 text-sm text-red-600">{errors.website}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Domain
                                </label>
                                <input
                                    type="text"
                                    name="domain"
                                    value={formData.domain}
                                    onChange={handleInputChange}
                                    className="w-full border border-input rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                    placeholder="example.com"
                                />
                            </div>
                        </div>

                        {/* Deduplication Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    VAT ID
                                </label>
                                <input
                                    type="text"
                                    name="vatId"
                                    value={formData.vatId}
                                    onChange={handleInputChange}
                                    className="w-full border border-input rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                    placeholder="ES12345678Z"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Tax ID
                                </label>
                                <input
                                    type="text"
                                    name="taxId"
                                    value={formData.taxId}
                                    onChange={handleInputChange}
                                    className="w-full border border-input rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                    placeholder="12345678Z"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Address Information */}
                <div className="bg-card rounded-lg shadow-sm border border-border">
                    <div className="px-6 py-4 border-b border-border">
                        <h3 className="text-lg font-semibold text-foreground">Address Information</h3>
                    </div>
                    <div className="p-6 space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Address Line 1 *
                            </label>
                            <input
                                type="text"
                                name="address.line1"
                                value={formData.address.line1}
                                onChange={handleInputChange}
                                className={`w-full border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring ${
                                    errors['address.line1'] ? 'border-red-300' : 'border-input'
                                }`}
                                placeholder="Street address"
                            />
                            {errors['address.line1'] && (
                                <p className="mt-1 text-sm text-red-600">{errors['address.line1']}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Address Line 2
                            </label>
                            <input
                                type="text"
                                name="address.line2"
                                value={formData.address.line2}
                                onChange={handleInputChange}
                                className="w-full border border-input rounded-lg px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                placeholder="Apartment, suite, unit, etc."
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    City *
                                </label>
                                <input
                                    type="text"
                                    name="address.city"
                                    value={formData.address.city}
                                    onChange={handleInputChange}
                                    className={`w-full border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring ${
                                        errors['address.city'] ? 'border-red-300' : 'border-input'
                                    }`}
                                    placeholder="City"
                                />
                                {errors['address.city'] && (
                                    <p className="mt-1 text-sm text-red-600">{errors['address.city']}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Region/State *
                                </label>
                                <input
                                    type="text"
                                    name="address.region"
                                    value={formData.address.region}
                                    onChange={handleInputChange}
                                    className={`w-full border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring ${
                                        errors['address.region'] ? 'border-red-300' : 'border-input'
                                    }`}
                                    placeholder="State/Region"
                                />
                                {errors['address.region'] && (
                                    <p className="mt-1 text-sm text-red-600">{errors['address.region']}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Postal Code *
                                </label>
                                <input
                                    type="text"
                                    name="address.postalCode"
                                    value={formData.address.postalCode}
                                    onChange={handleInputChange}
                                    className={`w-full border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring ${
                                        errors['address.postalCode'] ? 'border-red-300' : 'border-input'
                                    }`}
                                    placeholder="Postal code"
                                />
                                {errors['address.postalCode'] && (
                                    <p className="mt-1 text-sm text-red-600">{errors['address.postalCode']}</p>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Country *
                            </label>
                            <select
                                name="address.country"
                                value={formData.address.country}
                                onChange={handleInputChange}
                                className="w-full border border-input rounded-lg px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                            >
                                {countryOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Primary Contact */}
                <div className="bg-card rounded-lg shadow-sm border border-border">
                    <div className="px-6 py-4 border-b border-border">
                        <h3 className="text-lg font-semibold text-foreground">Primary Contact</h3>
                    </div>
                    <div className="p-6 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Contact Name *
                                </label>
                                <input
                                    type="text"
                                    name="primaryContact.name"
                                    value={formData.primaryContact.name}
                                    onChange={handleInputChange}
                                    className={`w-full border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring ${
                                        errors['primaryContact.name'] ? 'border-red-300' : 'border-input'
                                    }`}
                                    placeholder="Full name"
                                />
                                {errors['primaryContact.name'] && (
                                    <p className="mt-1 text-sm text-red-600">{errors['primaryContact.name']}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Contact Email *
                                </label>
                                <div className="relative">
                                    <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                                    <input
                                        type="email"
                                        name="primaryContact.email"
                                        value={formData.primaryContact.email}
                                        onChange={handleInputChange}
                                        className={`w-full pl-10 border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring ${
                                            errors['primaryContact.email'] ? 'border-red-300' : 'border-input'
                                        }`}
                                        placeholder="contact@example.com"
                                    />
                                </div>
                                {errors['primaryContact.email'] && (
                                    <p className="mt-1 text-sm text-red-600">{errors['primaryContact.email']}</p>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Contact Phone
                            </label>
                            <div className="relative">
                                <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                                <input
                                    type="tel"
                                    name="primaryContact.phoneE164"
                                    value={formData.primaryContact.phoneE164}
                                    onChange={handleInputChange}
                                    className={`w-full pl-10 border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring ${
                                        errors['primaryContact.phoneE164'] ? 'border-red-300' : 'border-input'
                                    }`}
                                    placeholder="+1234567890"
                                />
                            </div>
                            {errors['primaryContact.phoneE164'] && (
                                <p className="mt-1 text-sm text-red-600">{errors['primaryContact.phoneE164']}</p>
                            )}
                            <p className="mt-1 text-xs text-muted-foreground">
                                Include country code (e.g., +34 for Spain)
                            </p>
                        </div>
                    </div>
                </div>

                {/* Business Information */}
                <div className="bg-card rounded-lg shadow-sm border border-border">
                    <div className="px-6 py-4 border-b border-border">
                        <h3 className="text-lg font-semibold text-foreground">Business Information</h3>
                    </div>
                    <div className="p-6 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Lifecycle Stage
                                </label>
                                <select
                                    name="lifecycle"
                                    value={formData.lifecycle}
                                    onChange={handleInputChange}
                                    className="w-full border border-input rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                >
                                    {lifecycleOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Segment
                                </label>
                                <select
                                    name="segment"
                                    value={formData.segment}
                                    onChange={handleInputChange}
                                    className="w-full border border-input rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                >
                                    {segmentOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Priority
                                </label>
                                <select
                                    name="sla.priority"
                                    value={formData.sla.priority}
                                    onChange={handleInputChange}
                                    className="w-full border border-input rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                >
                                    {priorityOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Response Time (Hours)
                            </label>
                            <input
                                type="number"
                                name="sla.responseHours"
                                value={formData.sla.responseHours}
                                onChange={handleInputChange}
                                min="1"
                                max="168"
                                className="w-full border border-input rounded-lg px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                            />
                            <p className="mt-1 text-xs text-muted-foreground">
                                Expected response time in hours (1-168)
                            </p>
                        </div>
                    </div>
                </div>

                {/* Tags */}
                <div className="bg-card rounded-lg shadow-sm border border-border">
                    <div className="px-6 py-4 border-b border-border">
                        <h3 className="text-lg font-semibold text-foreground">Tags</h3>
                    </div>
                    <div className="p-6">
                        <div className="space-y-4">
                            <div className="flex flex-wrap gap-2 mb-4">
                                {formData.tags.map((tag, index) => (
                                    <span
                                        key={index}
                                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                                    >
                                        <TagIcon className="h-4 w-4" />
                                        {tag}
                                        <button
                                            type="button"
                                            onClick={() => handleTagRemove(tag)}
                                            className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500"
                                        >
                                            <XMarkIcon className="h-3 w-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                            <input
                                type="text"
                                placeholder="Add a tag and press Enter"
                                onKeyPress={handleTagAdd}
                                className="w-full border border-input rounded-lg px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                            />
                        </div>
                    </div>
                </div>

                {/* Consent */}
                <div className="bg-card rounded-lg shadow-sm border border-border">
                    <div className="px-6 py-4 border-b border-border">
                        <h3 className="text-lg font-semibold text-foreground">Consent & Communication</h3>
                    </div>
                    <div className="p-6 space-y-4">
                        <div className="space-y-3">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="consent.email"
                                    checked={formData.consent.email}
                                    onChange={handleInputChange}
                                    className="h-4 w-4 text-blue-600 focus:ring-ring border-input rounded"
                                />
                                <span className="ml-2 text-sm text-foreground">
                                    Email communications
                                </span>
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="consent.sms"
                                    checked={formData.consent.sms}
                                    onChange={handleInputChange}
                                    className="h-4 w-4 text-blue-600 focus:ring-ring border-input rounded"
                                />
                                <span className="ml-2 text-sm text-foreground">
                                    SMS communications
                                </span>
                            </label>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Lawful Basis
                            </label>
                            <select
                                name="consent.lawfulBasis"
                                value={formData.consent.lawfulBasis}
                                onChange={handleInputChange}
                                className="w-full border border-input rounded-lg px-3 py-2 b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                            >
                                <option value="consent">Consent</option>
                                <option value="contract">Contract</option>
                                <option value="legitimate_interest">Legitimate Interest</option>
                                <option value="vital_interest">Vital Interest</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Submit Buttons */}
                <div className="flex justify-end gap-3 pt-6">
                    <button
                        type="button"
                        onClick={() => navigate('/accounts')}
                        className="px-6 py-2 text-sm font-medium text-foreground bg-card border border-input bg-background text-foreground rounded-lg hover:bg-muted focus:outline-none focus:ring-2 f focus:ring-2 focus:ring-ring focus:border-ringocus:ring-ring"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {loading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                {isEditing ? 'Updating...' : 'Creating...'}
                            </>
                        ) : (
                            <>
                                <CheckCircleIcon className="h-4 w-4" />
                                {isEditing ? 'Update Account' : 'Create Account'}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AccountForm;